import {StyleSheet} from "aphrodite";
import {useCallback} from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {useGuideState} from "@khan/guide-state";
import {strings} from "@khan/guide-utils";

import {SoundWaves} from "./sound-waves.tsx";
import type {SpeechRecognitionResult} from "./use-speech-recognition.ts";

import microphoneIcon from "@phosphor-icons/core/fill/microphone-fill.svg";

const ON_INDICATOR_SIZE = 4;
const SOUND_WAVE_HEIGHT = 12;

interface Props {
    /**
     * Indicates whether the feature is on or off.
     */
    on: boolean;

    /**
     * The text that appears when the user hovers over the headphone button.
     */
    tooltip: string;

    /**
     * Indicates whether there is audio output.
     */
    speaking: boolean;

    /**
     * If true, the headphone button is disabled and cannot be clicked.
     */
    disabled?: boolean;

    /**
     * Optional style overrides that is applied to the button container.
     */
    style?: StyleType;

    /**
     * Callback that is triggered when the button is clicked.
     */
    onClick: () => void;
}

/**
 * A microphone button with a tooltip.
 *
 * This is used in the chat input component to enable speech recognition.
 */
export const MicrophoneButton = (props: Props) => {
    const {
        disabled = false,
        on = false,
        speaking,
        style,
        tooltip,
        onClick,
    } = props;
    return (
        <View style={[styles.container, style]}>
            <Tooltip content={tooltip}>
                <IconButton
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={tooltip}
                    icon={microphoneIcon}
                    role="switch"
                    aria-checked={on}
                    kind="tertiary"
                    size="small"
                />
            </Tooltip>
            {on && (
                <SoundWaves
                    color={disabled ? color.offBlack32 : color.blue}
                    style={[
                        styles.statusIndicator,
                        !speaking && styles.onIndicator,
                        disabled && styles.disabled,
                    ]}
                />
            )}
        </View>
    );
};

/**
 * Whereas {@link MicrophoneButton} is relatively "pure", which makes for nice
 * storybook stories, {@link ConnectedMicrophoneButton} hooks it up to our
 * GuideState context, and the {@link SpeechRecognitionResult} imperative object.
 */
export const ConnectedMicrophoneButton = ({
    speech,
    disabled,
    onEnable,
}: {
    speech: SpeechRecognitionResult;
    disabled: boolean;
    onEnable: () => void;
}) => {
    const threadId = useGuideState("threadId");
    const navigation = useGuideState("navigation");

    const handleToggleSpeechToText = useCallback(() => {
        if (disabled) {
            return;
        }
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideSpeechToTextToggled",
            eventSchemaVersion: 1,
            aiGuideThreadId: threadId ?? "",
            aiGuideNavigationId: navigation.id,
            isSpeechToTextOn: !speech.on,
        });
        if (!speech.on) {
            onEnable();
        }
        speech.toggleSpeechRecognition();
    }, [disabled, navigation, speech, threadId, onEnable]);

    return (
        <MicrophoneButton
            disabled={disabled}
            on={speech.on}
            speaking={speech.transcribing}
            tooltip={strings.microphoneIconTooltip(speech.on, disabled)}
            onClick={handleToggleSpeechToText}
        />
    );
};

const styles = StyleSheet.create({
    container: {
        flex: "0 0 auto",
        maxHeight: spacing.xLarge_32,
        alignItems: "center",
        justifyContent: "center",
    },
    disabled: {
        backgroundColor: color.offBlack32,
    },
    onIndicator: {
        position: "absolute",
        bottom: `calc(-${spacing.xxSmall_6}px - ${ON_INDICATOR_SIZE / 2}px)`,
        height: ON_INDICATOR_SIZE,
        width: ON_INDICATOR_SIZE,
        borderRadius: "50%",
        backgroundColor: color.blue,
    },
    statusIndicator: {
        transition: "all 300ms ease",
        position: "absolute",
        bottom: `calc(-${spacing.xxSmall_6}px - ${SOUND_WAVE_HEIGHT / 2}px)`,
        height: SOUND_WAVE_HEIGHT,
    },
});
